/**
 * Moves each row of the text band sideways while the band crosses the viewport.
 * Rows alternate direction through their data-direction attribute (1 = left, -1 = right).
 */
class AvalonTextBand extends HTMLElement {
  #rows = [];
  #distance = 0;
  #observer = null;
  #frame = 0;
  #listening = false;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#distance = Number(this.dataset.distance) || 0;
    this.#rows = Array.from(this.querySelectorAll('[data-direction]')).map((row) => ({
      track: row.querySelector('[data-track]'),
      direction: Number(row.dataset.direction) || 1,
      speed: Number(row.dataset.speed) || 1,
    }));

    this.#motionQuery.addEventListener('change', this.#onMotionChange);
    this.#observer = new IntersectionObserver(this.#onIntersect);
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#motionQuery.removeEventListener('change', this.#onMotionChange);
    this.#stopListening();
    cancelAnimationFrame(this.#frame);
  }

  get #enabled() {
    return this.#distance > 0 && !this.#motionQuery.matches;
  }

  #onIntersect = ([entry]) => {
    if (entry.isIntersecting && this.#enabled) {
      this.#startListening();
      this.#schedule();
    } else {
      this.#stopListening();
    }
  };

  #onMotionChange = () => {
    if (this.#enabled) return;

    this.#stopListening();
    this.#rows.forEach(({ track }) => {
      if (track) track.style.transform = '';
    });
  };

  #startListening() {
    if (this.#listening) return;

    window.addEventListener('scroll', this.#schedule, { passive: true });
    window.addEventListener('resize', this.#schedule, { passive: true });
    this.#listening = true;
  }

  #stopListening() {
    if (!this.#listening) return;

    window.removeEventListener('scroll', this.#schedule);
    window.removeEventListener('resize', this.#schedule);
    this.#listening = false;
  }

  #schedule = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    if (!this.#enabled) return;

    const rect = this.getBoundingClientRect();
    const travel = window.innerHeight + rect.height;
    const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / travel));
    const offset = (progress - 0.5) * this.#distance;

    this.#rows.forEach(({ track, direction, speed }) => {
      if (track) track.style.transform = `translate3d(${-direction * offset * speed}px, 0, 0)`;
    });
  }
}

if (!customElements.get('avalon-text-band')) {
  customElements.define('avalon-text-band', AvalonTextBand);
}
